/** Shared client-side validation rules (react-hook-form + step gating). */

import { SKILL_LIST } from '../constants/enquiryFormOptions';

export const validationRules = {
  'personal.fullName': { required: 'Full name is required' },
  'personal.dateOfBirth': { required: 'Date of birth is required' },
  'personal.gender': { required: 'Please select gender' },
  'personal.mobile': {
    required: 'Mobile number is required',
    pattern: {
      value: /^[6-9]\d{9}$/,
      message: 'Enter a valid 10-digit mobile number',
    },
  },
  'personal.email': {
    required: 'Email is required',
    pattern: {
      value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
      message: 'Enter a valid email address',
    },
  },
  'personal.address.city': { required: 'City is required' },
  'personal.address.state': { required: 'State is required' },
  'education.highestQualification': { required: 'Please select qualification' },
  'education.highestQualificationOther': {
    required: 'Please specify your qualification',
  },
  'education.currentStatus': { required: 'Please select current status' },
  'education.collegeName': { required: 'College / university is required' },
  'education.branch': { required: 'Branch / specialization is required' },
  'education.cgpaOrPercentage': {
    required: 'CGPA is required',
    min: { value: 0, message: 'CGPA must be between 0 and 10' },
    max: { value: 10, message: 'CGPA must be 10 or below' },
  },
  'work.totalExperience': { required: 'Please select experience level' },
  'coursePreference.courseInterested': { required: 'Please select a course' },
  'coursePreference.batchMode': { required: 'Please select batch mode' },
  'coursePreference.preferredStartMonth': {
    required: 'Preferred start month is required',
  },
  'coursePreference.hearAboutUs': { required: 'Please select an option' },
  'coursePreference.primaryGoalAfterCourse': {
    required: 'Please select your primary goal',
  },
  'careerGoals.whyJoin': { required: 'This field is required' },
  'careerGoals.careerGoal2To3Years': { required: 'This field is required' },
  'careerGoals.willingToRelocate': { required: 'Please select an option' },
  'careerGoals.expectedSalaryLpa': {
    min: { value: 0, message: 'Expected salary cannot be negative' },
    validate: (value) =>
      value === '' || value == null || Number(value) >= 0 || 'Expected salary cannot be negative',
  },
  'declaration.accepted': {
    validate: (value) => value === true || 'You must accept the declaration',
  },
};

const YEAR_OF_PASSING_MIN = 1990;
const YEAR_OF_PASSING_MAX_FUTURE = 2035;

export function validateYearOfPassing(value, context = {}) {
  if (value === '' || value == null) {
    return 'Year of passing is required';
  }

  const year = Number(value);
  if (Number.isNaN(year)) {
    return 'Enter a valid year';
  }

  if (year < YEAR_OF_PASSING_MIN || year > YEAR_OF_PASSING_MAX_FUTURE) {
    return 'Enter a valid year';
  }

  const currentYear = new Date().getFullYear();
  if (context.currentStatus !== 'student_pursuing' && year > currentYear) {
    return `Year of passing cannot be after ${currentYear}`;
  }

  return true;
}

export function fieldError(errors, name) {
  return name.split('.').reduce((acc, key) => acc?.[key], errors)?.message;
}

export function isFieldTouched(touchedFields, name) {
  const keys = name.split('.');
  let node = touchedFields;
  for (let i = 0; i < keys.length; i += 1) {
    const key = keys[i];
    if (!node || typeof node !== 'object') return false;
    if (i === keys.length - 1) return node[key] === true;
    node = node[key];
  }
  return false;
}

/** Show error after blur or when step-level validation runs (Next click). */
export function showFieldError(errors, touchedFields, name, showAllErrors = false) {
  const message = fieldError(errors, name);
  if (!message) return undefined;
  if (showAllErrors || isFieldTouched(touchedFields, name)) return message;
  return undefined;
}

export function getRules(name, context = {}) {
  if (name === 'education.highestQualificationOther') {
    return context.highestQualification === 'other'
      ? validationRules[name]
      : {};
  }
  if (name === 'education.yearOfPassing') {
    return {
      validate: (value) => validateYearOfPassing(value, context),
    };
  }
  if (/^skillRatings\.\d+\.level$/.test(name)) {
    return { required: 'Please select a rating for this skill' };
  }
  return validationRules[name] ?? {};
}

export function getSkillRatingFieldNames() {
  return SKILL_LIST.map((_, index) => `skillRatings.${index}.level`);
}

export function getStepFieldNames(step, context = {}) {
  const { highestQualification } = context;

  const steps = [
    [
      'personal.fullName',
      'personal.dateOfBirth',
      'personal.gender',
      'personal.mobile',
      'personal.email',
      'personal.address.city',
      'personal.address.state',
    ],
    [
      'education.highestQualification',
      ...(highestQualification === 'other'
        ? ['education.highestQualificationOther']
        : []),
      'education.currentStatus',
      'education.collegeName',
      'education.branch',
      'education.yearOfPassing',
      'education.cgpaOrPercentage',
      'work.totalExperience',
    ],
    [
      'coursePreference.courseInterested',
      'coursePreference.batchMode',
      'coursePreference.preferredStartMonth',
      'coursePreference.hearAboutUs',
      'coursePreference.primaryGoalAfterCourse',
      'careerGoals.whyJoin',
      'careerGoals.careerGoal2To3Years',
      'careerGoals.willingToRelocate',
      'careerGoals.expectedSalaryLpa',
    ],
    getSkillRatingFieldNames(),
    ['declaration.accepted'],
  ];

  return steps[step] ?? [];
}

function getNestedValue(obj, path) {
  return path.split('.').reduce((acc, key) => acc?.[key], obj);
}

function isFilled(value) {
  if (value === true) return true;
  if (value === false || value == null) return false;
  if (typeof value === 'number') return !Number.isNaN(value);
  return String(value).trim() !== '';
}

function validateField(name, value, context) {
  const rules = getRules(name, context);

  if (typeof rules.validate === 'function') {
    return rules.validate(value) === true;
  }

  if (rules.required && !isFilled(value)) return false;

  if (rules.pattern && isFilled(value) && !rules.pattern.value.test(String(value).trim())) {
    return false;
  }

  if (rules.min != null && isFilled(value) && Number(value) < rules.min.value) {
    return false;
  }

  if (rules.max != null && isFilled(value) && Number(value) > rules.max.value) {
    return false;
  }

  if (name === 'declaration.accepted' && value !== true) return false;

  return true;
}

export function isStepComplete(step, values, context = {}) {
  const fields = getStepFieldNames(step, context);
  if (fields.length === 0) return true;

  return fields.every((field) =>
    validateField(field, getNestedValue(values, field), context)
  );
}
